import React, { useEffect, useState } from "react";
import { Box, Icon, Link, Stack, Text, VStack } from "@chakra-ui/react";
import { GoHomeFill } from "react-icons/go";
import {
  BiSolidPlusSquare,
  BiSolidFile,
  BiSolidVideos,
  BiSolidData,
} from "react-icons/bi";
import { AiOutlinePoweroff } from "react-icons/ai";
import { MdAutoDelete, MdOutlineHttp, MdSettings } from "react-icons/md";
import { NavLink, useNavigate } from "react-router-dom";
import { AuthModel, CocModel } from "../../services/models";
import { FaCommentDots, FaUserPlus } from "react-icons/fa6";
import { FaUsers } from "react-icons/fa";
import { useOrder } from "../../context/OrderContext";

export default function Navbar() {
  const user = AuthModel.getUser();
  const navigate = useNavigate();
  const { reportUnSeenCount, setReportUnSeenCount } = useOrder();

  const handleLogout = () => {
    AuthModel.logout();
    navigate("/login");
  };

  useEffect(() => {
    const fetchUnSeenCount = async () => {
      try {
        let res = await CocModel.getUnSeenByUser();
        setReportUnSeenCount(res.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    if (["admin", "user"].includes(user.role)) {
      fetchUnSeenCount();
    }
  }, []);

  const navMenu =
    user.role === "admin"
      ? [
        {
          link: "/",
          icon: GoHomeFill,
          title: "Home",
        },
        {
          link: "/all-orders",
          icon: BiSolidFile,
          title: "All Orders",
          hasBadge: true,
        },
        {
          link: "/new-order",
          icon: BiSolidPlusSquare,
          title: "New Order",
        },
        {
          link: "/videos",
          icon: BiSolidVideos,
          title: "Videos",
        },
        {
          link: "/coc-delete-requests",
          icon: MdAutoDelete,
          title: "All Requests",
        },
        {
          link: "/users",
          icon: FaUsers,
          title: "Users",
        },
        {
          link: "/configuration",
          icon: MdSettings,
          title: "Settings",
        },
        {
          icon: AiOutlinePoweroff,
          title: "Logout",
          onClick: () => handleLogout(),
        },
      ]
      : [
        {
          link: "/all-orders",
          icon: BiSolidFile,
          title: "All Orders",
          hasBadge: true,
        },
        {
          link: "/new-order",
          icon: BiSolidPlusSquare,
          title: "New Order",
        },
        {
          link: "/technicians",
          icon: FaUserPlus,
          title: "Technicians",
        },
        {
          link: "/data-collections",
          icon: BiSolidData,
          title: "Collections",
        },
        ...(user.role === "user"
          ? [
              {
                link: "/external-integrations",
                icon: MdOutlineHttp,
                title: "External APIs",
              },
            ]
          : []),
        {
          link: "https://www.restechlabs.com.au/provide-your-feedback/",
          target: "_blank",
          icon: FaCommentDots,
          title: "Feedback",
        },
        {
          icon: AiOutlinePoweroff,
          title: "Logout",
          onClick: () => handleLogout(),
        },
      ];
  return (
    <Stack
      w={{ base: "full", lg: "120px" }}
      h={{ base: "auto", lg: "full" }}
      flexDirection={{ base: "row", lg: "column" }}
      justifyContent="center"
      alignItems="start"
      position={{ base: "fixed", lg: "relative" }}
      bottom={{ base: 0, lg: "auto" }}
      left={{ base: 0, lg: "auto" }}
      right={{ base: 0, lg: "auto" }}
      zIndex={99}
    >
      <Stack
        flexDirection={{ base: "row", lg: "column" }}
        w={{ base: "full", lg: "auto" }}
        justifyContent={{
          base: "space-evenly",
          md: "space-evenly",
          lg: "center",
        }}
        bgColor="white"
        shadow={{ base: "0px 0px 6px 0px rgb(0,0,0,0.1)", lg: "md" }}
        roundedTopLeft={{ base: "3xl", lg: "0" }}
        roundedTopRight={"3xl"}
        roundedBottomRight={{ md: "0", lg: "3xl" }}
        py="4"
        px="3"
        gap={{ md: "3", lg: "2" }}
        overflowX={"auto"}
      >
        {navMenu.map((navItem, i) => {
          if (navItem.onClick) {
            return (
              <VStack
                key={i}
                onClick={navItem.onClick}
                gap="0"
                cursor="pointer"
                role="group"
                minW={{ base: "90px", md: "auto" }}
              >
                <Box
                  bgColor="red.50"
                  rounded="full"
                  w="fit-content"
                  px="2"
                  pt="2"
                  pb="0.5"
                  _groupHover={{ backgroundColor: "red" }}
                >
                  <Icon
                    as={navItem.icon}
                    color="red"
                    fontSize="2xl"
                    _groupHover={{ color: "white" }}
                  />
                </Box>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray"
                  _groupHover={{ color: "red" }}
                >
                  {navItem.title}
                </Text>
              </VStack>
            );
          } else {
            return (
              <Link
                key={i}
                as={NavLink}
                to={navItem.link}
                target={navItem.target ?? ""}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textDecoration: "none",
                }}
                role="group"
                _activeLink={{
                  "& > .icon": { backgroundColor: "primary.main" },
                  "& > .icon svg": { color: "white" },
                  "& p": { color: "primary.main" },
                }}
                minW={{ base: "90px", md: "auto" }}
                position="relative"
              >
                {["admin", "user"].includes(user.role) && navItem.hasBadge &&
                  reportUnSeenCount ? (
                  <Box
                    bgColor="red.500"
                    rounded="full"
                    color="white"
                    fontSize="xs"
                    px="1"
                    minW="18px"
                    textAlign="center"
                    position="absolute"
                    top="0"
                    right="3"
                  >
                    {reportUnSeenCount < 100 ? reportUnSeenCount : "100+"}
                  </Box>
                ) : null}
                <Box
                  className="icon"
                  bgColor="primary.100"
                  rounded="full"
                  w="fit-content"
                  px="2"
                  pt="2"
                  pb="0.5"
                  _groupHover={{ backgroundColor: "primary.main" }}
                >
                  <Icon
                    as={navItem.icon}
                    color="primary.main"
                    fontSize="2xl"
                    _groupHover={{ color: "white" }}
                  />
                </Box>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray"
                  _groupHover={{
                    color: "primary.main",
                  }}
                >
                  {navItem.title}
                </Text>
              </Link>
            );
          }
        })}
      </Stack>
    </Stack>
  );
}
